import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { AllCommunityModule, type StateUpdatedEvent, type GridApi, type GridReadyEvent, type IRowNode } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Input, Button, FormControlLabel, FormControl, FormGroup, Radio, RadioGroup, Checkbox, MenuItem, Select, Tooltip } from "@mui/material";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";

import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import router from "@user/router";
import { useTabsStore } from "@user/store/tabs_store";
import { useAuthStore } from "@user/store/auth_store";
import { useSettingsStore } from "@user/store/settings_store";

import "./backup_work.scoped.scss";

import BackupStockInfoPopup from "@user/views/inoutbound/popup_backup_stock_info";
import BackupworkPopup from "@user/views/inoutbound/popup_backupwork";
import TempworkPopup from "@user/views/inoutbound/popup_tempwork";
import SkuReqPopup from "@user/views/inoutbound/popup_sku_req";

import PrintTicketTemp, { type PrintTicketTempHandle } from "@user/views/print/print_ticket_temp";

interface State {
  gridApi: GridApi | null,
  priorityType: string,
  workStationId: string,
  workReqInfos: any[],
  isShowBackupStockInfo: boolean,
  isShowBackupwork: boolean,
  isShowTempwork: boolean,
  isShowSkuReq: boolean
}

const defState: State = {
  gridApi: null,
  priorityType: "N",
  workStationId: "",
  workReqInfos: [],
  isShowBackupStockInfo: false,
  isShowBackupwork: false,
  isShowTempwork: false,
  isShowSkuReq: false
};

interface SearchParams {
  workReqEtcType: string,
  inoutTypes: string,
  workReqStatuses: string,
  wh: string,
  pnLike: string,
  woLike: string,
  workShopLike: string,
  requesterLike: string,
  workOrdererNameLike: string,
  page: number,
  pageSize: number
}

const defSearchParams: SearchParams = {
  workReqEtcType: "",
  inoutTypes: "",
  workReqStatuses: "",
  wh: "",
  pnLike: "",
  woLike: "",
  workShopLike: "",
  requesterLike: "",
  workOrdererNameLike: "",
  page: 1,
  pageSize: kutil.DEFAULT_PAGE_SIZE
};

interface Result {
  searchParams: SearchParams,
  pageInfo: PageInfo,
  infos: any[]
}

const defResult = {
  searchParams: kutil.deepCopy(defSearchParams),
  pageInfo: {
    totalCount: 0,
    totalPage: 0,
    page: defSearchParams.page,
    pageSize: defSearchParams.pageSize
  },
  infos: []
};

const validationMsg = [
  {id: "workReqEtcNo", type: "InvalidStatus", msg: "작업대기 상태만 작업지시할 수 있습니다."},
  {id: "workInfos", type: "NotFound", msg: "선택SKU가 있는 작업만 작업지시할 수 있습니다."},
  {id: "skuNo", type: "InvalidStatus", msg: "현재 작업대에서 호출할 수 없는 SKU입니다."}
];

function BackupWork(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const isActiveTab = useTabsStore((state) => state.currTabId == props.id);
  const location = useTabsStore((state) => state.getLocation(props.id));
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(location.path), [location.path]);

  const printTicketTempRef = useRef<PrintTicketTempHandle>(null);

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "구분", field: "workReqEtcType", width: 80, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("workReqEtcType") },
    { headerName: "작업구분", field: "inoutType", width: 80, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("inoutType") },
    { headerName: "작업상태", field: "workReqStatus", width: 80, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("workReqStatus") },
    { headerName: "선택SKU", field: "choiceSkuType", width: 80, cellStyle: {textAlign: "center"},
      valueGetter: (p: any) => {
        if (p.data.choiceSkuType != null && p.data.choiceSkuCallQty != null && p.data.choiceSkuStockQty != null) {
          return (p.data.choiceSkuType + "(" + p.data.choiceSkuCallQty + "/" + p.data.choiceSkuStockQty +")");
        }
      },
    },
    { headerName: "Creat Date", field: "createDate", width: 180, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.date("yyyy-MM-dd HH:mm:ss"), filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "창고", field: "wh", width: 80, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("wh") },
    { headerName: "P/N", headerClass: "hd-ta-left", field: "pn", width: 160, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "TR No", headerClass: "hd-ta-left", field: "trNo", width: 150, cellStyle: { textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "S/N", headerClass: "hd-ta-left", field: "sn", width: 150, cellStyle: { textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "KWD", headerClass: "hd-ta-left", field: "kwd", width: 180, cellStyle: { textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]}},
    { headerName: "CTG", headerClass: "hd-ta-left", field: "ctg", width: 120, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "W/O", headerClass: "hd-ta-left", field: "wo", width: 150, cellStyle: { textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Work Shop", headerClass: "hd-ta-left", field: "workShop", width: 120, cellStyle: { textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Request Qty", headerClass: "hd-ta-right", field: "reqQty", width: 130, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Remain Qty", headerClass: "hd-ta-right", field: "remainQty", width: 130, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Complete Qty", headerClass: "hd-ta-right", field: "completeQty", width: 130, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "UOM", field: "uom", width: 80, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "요청자", field: "requesterName", width: 100, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "작업자", field: "workOrdererName", width: 100, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "티켓출력", field: "", width: 80, cellStyle: kutil.aggridButtonCellStyle,
      cellRenderer: (p: any) => {
        if (p.data.workReqEtcType == "T") {
          if (!auth.download) return null;
          return (<button className="icon-button grid-print" onClick={() => printTicketTemp(p.data, 1)}>출력</button>);
        }
      }
    },
    { headerName: "작업대", field: "workStationId", width: 80, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("workStationId") }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridOptions = useMemo(() => ({
    rowSelection: {
      mode: "multiRow" as const,
      isRowSelectable: (n: IRowNode) => {
        return (
          (n.data.workReqStatus == "W")
        );
      },
      hideDisabledCheckboxes: true
    },
    selectionColumnDef: {
      resizable: true
    }
  }), []);
  const gridCallbacks = useStableHandlers({
    onGridReady: (e: GridReadyEvent) => {
      setState(prev => ({ ...prev, gridApi: e.api }));
    },
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, location.path);
    },
  });

  // location 변경시 처리
  useEffect(() => {
    init();
  }, [location.key]);

  // 바코드 스캔 기능 적용
  const onBarcodeScanRef = useRef(onBarcodeScan);
  useEffect(() => {
    onBarcodeScanRef.current = onBarcodeScan;
  });

  useEffect(() => {
    if (!isActiveTab || state.isShowBackupStockInfo || state.isShowBackupwork || state.isShowTempwork || state.isShowSkuReq) {
      return;
    }

    const handler = (code: string) => onBarcodeScanRef.current(code);
    kutil.startBarcodeScan(handler);
    return () => kutil.stopBarcodeScan(handler);
  }, [isActiveTab, state.isShowBackupStockInfo, state.isShowBackupwork, state.isShowTempwork, state.isShowSkuReq]);
  // 끝: 바코드 스캔 기능 적용

  // 작업대ID 기본값 지정
  useEffect(() => {
    const workStationId = useSettingsStore.getState().getDefWorkStationId(location.path) || (commonCode.workStationId as any[])?.[0]?.code || "";
    setState(prev => ({ ...prev, workStationId}));
  }, [location.path]);

  // 변경 작업대ID 저장
  useEffect(() => {
    if (state.workStationId) {
      useSettingsStore.getState().setLocalSetting("workStationId", location.path, state.workStationId);
    }
  }, [state.workStationId]);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfos();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const searchParams = kutil.deepCopy(defSearchParams);
    kutil.copyObj(Object.fromEntries(new URLSearchParams(location.search).entries()), searchParams);

    if (!("inoutTypes" in Object.fromEntries(new URLSearchParams(location.search)))) {
      searchParams.inoutTypes = (commonCode.inoutType as any[])?.map(c => c.code).join(",");
    }
    if (!("workReqStatuses" in Object.fromEntries(new URLSearchParams(location.search)))) {
      searchParams.workReqStatuses = (commonCode.workReqStatus as any[])?.filter(c => c.code !== "CA").map(c => c.code).join(",");
    }
    updateSearchParams(searchParams);
  }

  async function searchInfos() {
    const searchParams = searchParamsRef.current;

    try {
      const reqSearchParams = kutil.copyObj(searchParams, {} as SearchParams);
      const data = await kutil.sendGet("/api/inoutbound/backup_work.data?mode=search", reqSearchParams, {});
      setResult({
        searchParams: reqSearchParams,
        pageInfo: data.pageInfo,
        infos: data.infos,
      });
    } catch(e) {
      console.log(e);
    }
  }

  async function doSearch(_e: any, reqParams: any|null = null) {
    const searchParams = updateSearchParams({ ...reqParams, page: 1 });
    router.navigateByPath("/inoutbound/backup_work", searchParams);
  }

  const goPage = useCallback((page: number) => {
    const searchParams = updateSearchParams({ ...result.searchParams, page }, true);
    router.navigateByPath("/inoutbound/backup_work", searchParams);
  }, [result, router, updateSearchParams]);

  async function resetParams() {
    router.navigateByPath("/inoutbound/backup_work", {});
  }

  async function changeInoutType(e: any) {
    doSearch(e, {inoutTypes: kutil.joinCheckBoxArr(searchParams.inoutTypes, e.target.value, e.target.checked)});
  }

  async function changeWorkReqStatus(e: any) {
    doSearch(e, {workReqStatuses: kutil.joinCheckBoxArr(searchParams.workReqStatuses, e.target.value, e.target.checked)});
  }

  async function orderWork() {
    const selectedInfos = state.gridApi!.getSelectedRows();
    if (selectedInfos.length === 0) {
      await kutil.alert("작업지시 작업을 선택해 주세요.");
      return;
    }

    if (await kutil.confirm("작업지시하시겠습니까?") === false) {
      return;
    }

    const multiInfos: any[] = [];

    for (const info of selectedInfos) {
      try {
        const data = await kutil.sendGet("/api/inoutbound/backup_work.data?mode=searchSkuStock", { mngType: info.mngType, wh: info.wh, pn: info.pn, trNo: info.trNo, sn: info.sn, workStationId: state.workStationId }, {});
        info.workStationId = state.workStationId;
        info.priorityType = state.priorityType;
        info.trSn = (info.trNo || "") + (info.sn || "");

        if (data.infos.length === 0) {
          await kutil.alert("SKU가 없습니다.");
          continue;
        }

        if (!data.infos.some((row: any) => row.outPossYn == "Y")) {
          await kutil.alert("현재 작업대에서 호출할 수 없는 SKU입니다.");
          continue;
        }

        if (data.infos.length === 1) {
          const skuStock = data.infos[0];
          if (info.inoutType === "O" && Number(info.remainQty) > Number(skuStock.stockQty)) {
            await kutil.alert("재고수량을 초과하였습니다.");
            continue;
          }
          info.choiceSkuType = "S";
          info.choiceSkuCallQty = "1";
          info.choiceSkuStockQty = "1";
          info.workInfos = data.infos;
          info.workInfos[0].jobQty = info.remainQty;
          await kutil.sendPost("/api/inoutbound/backup_work.proc?mode=orderWork", info, {
            validationMsg: validationMsg
          });
          continue;
        }

        const stockQty = data.infos.filter((row: any) => row.outPossYn == "Y").reduce((sum: number, row: any) => sum + (Number(row.stockQty) || 0), 0);
        if (info.inoutType === "O" && (stockQty === 0 || Number(info.remainQty) > stockQty)) {
          await kutil.alert("재고수량을 초과하였습니다.");
          continue;
        }

        info.skuStockInfos = data.infos;
        multiInfos.push(info);
      } catch(e) {
        console.log(e);
      }
    }

    if (multiInfos.length > 0) {
      openPopupBackupStockInfo(multiInfos);
    } else {
      setState(prev => ({ ...prev, priorityType: "N" }));
      router.reload();
    }
  }

  async function reqSku() {
    openPopupSkuReq();
  }

  async function addBackupWork() {
    openPopupBackupwork();
  }

  async function addTempWork() {
    openPopupTempwork();
  }

  async function printTicketTemp(info: any, copies: number) {
    try {
      const data = await kutil.sendGet("/api/inoutbound/backup_work.data?mode=searchStockQty", info, {});
      info.stockQty = data.stockQty;
      await printTicketTempRef.current?.print(info, copies);
    } catch(e) {
      console.log(e);
    }
  }

  async function openPopupBackupStockInfo(infos: any[]) {
    setState(prev => ({ ...prev, isShowBackupStockInfo: true, workReqInfos: infos}));
  }

  async function closePopupBackupStockInfo(data: any) {
    setState(prev => ({ ...prev, isShowBackupStockInfo: false }));

    if (data?.isSuccess) {
      setState(prev => ({ ...prev, priorityType: "N" }));
      router.reload();
    }
  }

  async function openPopupBackupwork() {
    setState(prev => ({ ...prev, isShowBackupwork: true }));
  }

  async function closePopupBackupwork(data: any) {
    setState(prev => ({ ...prev, isShowBackupwork: false }));

    if (data?.isSuccess) {
      router.reload();
    }
  }

  async function openPopupTempwork() {
    setState(prev => ({ ...prev, isShowTempwork: true }));
  }

  async function closePopupTempwork(data: any) {
    setState(prev => ({ ...prev, isShowTempwork: false }));

    if (data?.isSuccess) {
      await printTicketTemp(data.info, 2);
      router.reload();
    }
  }

  async function openPopupSkuReq() {
    setState(prev => ({ ...prev, isShowSkuReq: true }));
  }

  async function closePopupSkuReq(_data: any) {
    setState(prev => ({ ...prev, isShowSkuReq: false }));
  }

  async function onBarcodeScan(code: string) {
    const [prefix, ...rest] = code.split(":");
    const value = rest.join(":").trim();

    if (rest.length > 0) {
      switch (prefix) {
        case "WS": {
          if (!value) return;
          const exists = (commonCode.workStationId as any[]).some(c => c.code === value);
          if (!exists) {
            kutil.alert("일치하는 작업대가 없습니다.\r\n" + code);
            return;
          }
          setState(prev => ({ ...prev, workStationId: value }));
          return;
        }
        default:
          return;
      }
    }
  }

  return (
    <>
      {/* contents */}
      <div className="contents">
        {/* contents 상단 */}
        <div className="contents-top">
          {/* 타이틀 영역 */}
          <div className="title-area">
            {/* 타이틀 */}
            <p>BackUp 작업</p>
          </div>
          {/*// 타이틀 영역 */}
          {/* 버튼 영역 */}
          <div className="top-btn-area">
            <Button variant="outlined" onClick={doSearch}>조회</Button>
            { auth.write &&
            <Button variant="outlined" onClick={addBackupWork}>BackUp작업</Button>
            }
            { auth.write &&
            <Button variant="outlined" onClick={addTempWork}>임시작업</Button>
            }
            { auth.write &&
            <Button variant="outlined" onClick={reqSku}>범위지정</Button>
            }
            { auth.write &&
            <Button variant="contained" onClick={orderWork}>작업지시</Button>
            }
          </div>
          {/*// 버튼 영역 */}
        </div>
        {/*// contents 상단 */}
        {/* 검색 */}
        <div className="work-srch-area">
          <div>
            <p>작업구분</p>
            <FormControl>
              <RadioGroup
                value={searchParams.workReqEtcType}
                onChange={(e) => {doSearch(e, {workReqEtcType: e.target.value})}}
              >
                <FormControlLabel value="" control={<Radio size="small" />} label="전체" />
                {(commonCode.workReqEtcType as any[]).map((code) => (
                  <FormControlLabel key={code.code} value={code.code} control={<Radio size="small" />} label={code.value} />
                ))}
              </RadioGroup>
            </FormControl>
          </div>
          <div className="divider ver"></div>
          <div>
            <p>상세구분</p>
            <FormGroup>
                {(commonCode.inoutType as any[]).map((code) => (
                  <FormControlLabel key={code.code} value={code.code} control={<Checkbox size="small" checked={searchParams.inoutTypes.split(",").includes(code.code)} />} label={code.value} onChange={changeInoutType} />
                ))}
            </FormGroup>
          </div>
          <div className="divider ver"></div>
          <div>
            <p>작업상태</p>
            <FormGroup>
                {(commonCode.workReqStatus as any[]).filter(c => c.code !== "CA").map((code) => (
                  <FormControlLabel key={code.code} value={code.code} control={<Checkbox size="small" checked={searchParams.workReqStatuses.split(",").includes(code.code)} />} label={code.value} onChange={changeWorkReqStatus} />
                ))}
            </FormGroup>
          </div>
          <div className="divider ver"></div>
          <div className="srch_item">
            <div>
              <p>창고</p>
              <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                <Select
                  value={searchParams.wh}
                  onChange={(e) => {doSearch(e, {wh: e.target.value});}}
                  displayEmpty
                >
                  <MenuItem value="">전체</MenuItem>
                  {(commonCode.wh as any[]).map((code) => (
                    <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div>
              <p>P/N</p>
              <Input
                inputProps={{ maxLength: 100 }}
                value={searchParams.pnLike}
                onChange={(e) => {updateSearchParams({ pnLike: e.target.value })}}
                onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
                placeholder="P/N"
              />
            </div>
            <div>
              <p>W/O</p>
              <Input
                inputProps={{ maxLength: 100 }}
                value={searchParams.woLike}
                onChange={(e) => {updateSearchParams({ woLike: e.target.value })}}
                onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
                placeholder="W/O"
              />
            </div>
            <div>
              <p>Work Shop</p>
              <Input
                inputProps={{ maxLength: 100 }}
                value={searchParams.workShopLike}
                onChange={(e) => {updateSearchParams({ workShopLike: e.target.value })}}
                onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
                placeholder="Work Shop"
              />
            </div>
            <div>
              <p>요청자</p>
              <Input
                inputProps={{ maxLength: 100 }}
                value={searchParams.requesterLike}
                onChange={(e) => {updateSearchParams({ requesterLike: e.target.value })}}
                onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
                placeholder="요청자"
              />
            </div>
            <div>
              <p>작업자</p>
              <Input
                inputProps={{ maxLength: 100 }}
                value={searchParams.workOrdererNameLike}
                onChange={(e) => {updateSearchParams({ workOrdererNameLike: e.target.value })}}
                onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
                placeholder="작업자"
              />
            </div>
          </div>
          <p>
            <Tooltip title="초기화">
              <button className="icon-button form-reset" onClick={resetParams}>초기화</button>
            </Tooltip>
          </p>
        </div>
        {/*// 검색 */}
        {/* 그리드 */}
        <AgGridProvider modules={modules}>
          <div className="grid-area">
            <CustomAgGridReact
              columnDefs={gridInfo}
              rowData={result.infos}
              defaultColDef={kutil.defaultColDef}
              initialState={gridState}
              rowSelection={gridOptions.rowSelection}
              selectionColumnDef={gridOptions.selectionColumnDef}
              onGridReady={gridCallbacks.onGridReady}
              onStateUpdated={gridCallbacks.onStateUpdated}
              totalPage={result.pageInfo.totalPage}
              currPage={result.pageInfo.page}
              onPageChange={goPage}
            />
          </div>
        </AgGridProvider>
        {/*// 그리드 */}
        {state.isShowBackupStockInfo && <BackupStockInfoPopup isShow={state.isShowBackupStockInfo} onClose={closePopupBackupStockInfo} id={props.id} data={{infos: state.workReqInfos}} />}
        {state.isShowBackupwork && <BackupworkPopup isShow={state.isShowBackupwork} onClose={closePopupBackupwork} id={props.id} data={{workStationId: state.workStationId}} />}
        {state.isShowTempwork && <TempworkPopup isShow={state.isShowTempwork} onClose={closePopupTempwork} id={props.id} data={{workStationId: state.workStationId}} />}
        {state.isShowSkuReq && <SkuReqPopup isShow={state.isShowSkuReq} onClose={closePopupSkuReq} id={props.id} data={{path: "backup_work", workStationId: state.workStationId}} />}
        <PrintTicketTemp ref={printTicketTempRef} />
        {/* 옵션 */}
        <div className="work_option">
          <div>
            <p>우선순위</p>
            <FormControl sx={{ m: 1, minWidth: "100%" }} size="small">
              <Select
                value={state.priorityType}
                onChange={(e) => {setState(prev => ({ ...prev, priorityType: e.target.value }))}}
                displayEmpty
              >
                {(commonCode.priorityType as any[]).map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className="divider ver"></div>
          <div>
            <p>작업대</p>
            <FormControl sx={{ m: 1, minWidth: "100%" }} size="small">
              <Select
                value={state.workStationId}
                onChange={(e) => {setState(prev => ({ ...prev, workStationId: e.target.value }))}}
                displayEmpty
              >
                {(commonCode.workStationId as any[]).map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>
        {/*// 옵션 */}
      </div>
      {/*// contents */}
    </>
  );
}

export default React.memo(BackupWork);
